import { useEffect, useState, useRef, useMemo } from "react";
import { FiCopy, FiCheck } from "react-icons/fi";
// import { FiChevronDown } from "react-icons/fi";
import { toastMessage } from "@/utils/helper/myToast";
import Button from "@/common/ui/Button";
import { fetchEmployeeShareLink, useEmployeeInfo } from "@/services/employee.services";
// import { useEmployeePlans } from "@/services/employee.services";
import { RoundLoader } from "@/common/ui/Loader";
import Checkbox from "@/common/custom/Chekbox";
import { motion } from "framer-motion";
import { getNestedValue } from "@/utils/helper/form";
import { memo } from "react";
import CustomLink from "@/common/ui/CustomLink";
import { formateCurrency } from "@/utils/helper/string";
// import useClickOutside from "@/hooks/useClickOutSide";

const fieldConfigs = [
  { label: "Employee name", value: "empName.label", isDefault: true, id: "fullName", isRequired: "all" },
  { label: "Employee ID", value: "empId", id: "empId", isRequired: "all" },
  { label: "Designation", value: "designation.label", id: "designation", isRequired: "all" },
  { label: "Employment type", value: "empTyp.label", id: "employmentType", isRequired: "all" },
  { label: "CTC", value: "ctc.view", id: "ctc", isRequired: "all" },
  { label: "Employment period", value: "employmentPeriod", id: "employmentPeriod", isRequired: "exit" },
  { label: "Reason for leaving", value: "leavingReason", id: "leavingReason", isRequired: "exit" },
  { label: "Conduct and work ethics", value: "conductEthics", id: "conductEthics", isRequired: "exit" },
  { label: "Eligible to rehire", value: "rehireEligibility", id: "rehireEligibility", isRequired: "exit" },
  { label: "Clearance status", value: "clearanceStatus", id: "clearanceStatus", isRequired: "exit" },
  { label: "Relieving letter issued", value: "relievLetterIsud", id: "relievingLetterIssued", isRequired: "exit" },
  { label: "Notice period served", value: "noticeServed", id: "noticeServed", isRequired: "exit" },
  { label: "Reporting manager", value: "reportingMngr.label", id: "reportingMngr", isRequired: "all" },
  { label: "Last drawn salary", value: "lstDrawnSalary.view", id: "lastDrawnSalary", isRequired: "exit" },
  { label: "Additional Information", value: "additionalInfo", id: "additionalInfo", isCustomInput: true, isRequired: "all" },
  // { label: "Doc Price", value: "docPrice", id: "docPrice", isDropdown: true, isRequired: "all" },
  // { label: "Supporting documents", value: "exitDoc.url", id: "supportingDoc" },
];

interface RenderLinkProps {
  onClose: () => void;
  empId: string;
  fields: any;
  AdditionalInfo?: string;
  docPlanId?: number;
}

const RenderLink: React.FC<RenderLinkProps> = memo(({ empId, fields, onClose, AdditionalInfo, docPlanId }) => {
  const [copied, setCopied] = useState(false);
  const [shareData, setShareData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handleCopy = () => {
    if (shareData?.shareLink) {
      navigator.clipboard.writeText(shareData.shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toastMessage("Link copied to clipboard", false);
    }
  };

  const handleShare = async () => {
    if (navigator.share && shareData?.shareLink) {
      try {
        await navigator.share({
          title: "Employee Link",
          text: "Here is the employee link",
          url: shareData?.shareLink,
        });
      } catch (err) {
        console.log("Share failed:", err);
      }
    } else {
      handleCopy();
    }
  };
  const handleGenerateShareLink = async () => {
    try {
      setLoading(true);
      const requestData: any = { empId, fields };
      
      // Add additional info if it exists
      if (AdditionalInfo && AdditionalInfo.trim()) {
        requestData.AdditionalInfo = AdditionalInfo.trim();
      }
      
      // Add doc plan id if it exists
      if (docPlanId) {
        requestData.docPlanId = docPlanId;
      }
      
      const res: any = await fetchEmployeeShareLink(requestData);
      if (res?.shareLink) {
        setShareData(res);
      }
    } catch (e: any) {
      if (e?.response?.data) {
        const { message, isError, showMessage } = e.response.data || {};
        if (message) {
          onClose();
          return showMessage && toastMessage(message, isError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      handleGenerateShareLink();
    }
  }, []);

  if (loading)
    return (
      <motion.div
        className="bg-white/70 backdrop-blur flex items-center justify-center z-10 rounded-lg h-300"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <RoundLoader />
      </motion.div>
    );
  return (
    <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
      <div className="flex items-center border rounded px-3 py-2 mb-6 bg-gray-50">
        <input className="flex-1 bg-transparent outline-none text-gray-700 text-13" defaultValue={shareData?.shareLink} readOnly />
        <button className="ml-2 text-gray-500 hover:text-gray-700" onClick={handleCopy} title="Copy link">
          {copied ? <FiCheck size={20} className="text-green-500" /> : <FiCopy size={20} />}
        </button>
      </div>

      <div className="flex justify-end">
        <div className="flex justify-between gap-4">
          <button className="px-4 py-1.5 rounded text-13 border text-gray-700 hover:bg-gray-100" onClick={onClose}>
            Cancel
          </button>
          <Button onClick={handleShare} variant={"primary"} size="small" disabled={!shareData?.shareLink}>
            Copy link and share
          </Button>
        </div>
      </div>
    </div>
  );
});

const ShareLink = ({ employeeId, onClose }: any) => {
  const { isLoading, data } = useEmployeeInfo({ empId: employeeId, view_typ: "exit" });
  const [step, setStep] = useState<"field" | "">("field");
  const [AdditionalInfo, setAdditionalInfo] = useState<string>("");
  const [docPlanId, setDocPlanId] = useState<number | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  // const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Only fetch plans when dropdown is opened
  // const { data: plansData, isLoading: plansLoading, error: plansError } = useEmployeePlans(isDropdownOpen);
  
  // Determine if employee is ex-employee or active using exit API data
  const isExEmployee = data?.status?.label === "Ex-employee";
  
  // Filter fields based on employee status
  const filteredFields = useMemo(() => {
    return fieldConfigs.filter((field) => {
      if (isExEmployee) {
        // Ex-employee: show fields with isRequired "all" or "exit"
        return field.isRequired === "all" || field.isRequired === "exit";
      } else {
        // Active employee: show only fields with isRequired "all"
        return field.isRequired === "all";
      }
    });
  }, [isExEmployee]);
  
  // Initialize selectedFields with default fields from filtered list
  const [selectedFields, setSelectedFields] = useState<string[]>(() => 
    fieldConfigs.filter((f) => f.isDefault).map((f) => f.value)
  );

  // Update selectedFields when filteredFields changes
  useEffect(() => {
    const defaultFields = filteredFields.filter((f) => f.isDefault).map((f) => f.value);
    const availableFieldValues = filteredFields.map((f) => f.value);
    
    setSelectedFields((prev) => {
      // Filter out any selected fields that are no longer available
      const validSelectedFields = prev.filter((field) => availableFieldValues.includes(field));
      
      // Use default fields if no valid fields remain, or merge with defaults
      const hasChanged = defaultFields.length !== validSelectedFields.length || 
        !defaultFields.every((field) => validSelectedFields.includes(field));
      
      return hasChanged && validSelectedFields.length === 0 
        ? defaultFields 
        : validSelectedFields.length > 0 
          ? validSelectedFields 
          : defaultFields;
    });
  }, [filteredFields]);

  const toggleField = (field: string) => {
    setSelectedFields((prev) => {
      const newSelectedFields = prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field];
      
      // Clear additional info when deselecting the additionalInfo field
      if (field === "additionalInfo" && prev.includes(field)) {
        setAdditionalInfo("");
      }
      
      // Clear doc plan id when deselecting the docPrice field
      if (field === "docPrice" && prev.includes(field)) {
        setDocPlanId(null);
      }
      
      return newSelectedFields;
    });
  };

  //Add Price code

  // const dropdownRef = useClickOutside(() => setIsDropdownOpen(false));

  // // Transform plans data to dropdown options
  // const planOptions = useMemo(() => {
  //   if (!plansData || !Array.isArray(plansData)) return [];
  //   return plansData.map((plan) => ({
  //     id: plan.id,
  //     label: `${plan.currency || ''}${plan.price || ''}`,
  //     value: plan.id,
  //     price: plan.price,
  //     currency: plan.currency,
  //   }));
  // }, [plansData]);

  if (step === "field") {
    return (
      <motion.div
        className="bg-white rounded-lg shadow-lg w-full max-w-md p-2 px-4 relative"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {isLoading ? (
          <motion.div
            className="bg-white/70 backdrop-blur flex items-center justify-center z-10 rounded-lg h-300"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <RoundLoader />
          </motion.div>
        ) : (
          <>
            <h2 className="text-13 font-semibold my-3 text-text-default">Select fields to share</h2>
            <div className="grid bg-white max-h-[480px] overflow-y-auto pr-2">
              {filteredFields.map((field, index) => {
                let defaultValue = getNestedValue(data, field.value);
                if (defaultValue && (field.id === "lastDrawnSalary" || field.id === "ctc")) {
                  defaultValue = `${defaultValue[0]} ${formateCurrency(defaultValue)}`;
                }
                return (
                  <motion.div
                    key={field.value}
                    className="flex flex-col text-13 py-2"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <label className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedFields.includes(field.value)}
                        onChange={() => toggleField(field.value)}
                        disabled={field.isDefault}
                        className="shrink-0"
                      />
                      <div className="flex flex-col gap-1">
                        <h5 className="text-xs font-bold text-text-default">{field.label}</h5>
                        {!field.isCustomInput && (
                          <>
                            {field.label === "Supporting documents" ? (
                              defaultValue ? (
                                <CustomLink link={{ text: "View document", value: defaultValue }} isExternal className="text-13" />
                              ) : (
                                "-"
                              )
                            ) : (
                              <span className="text-13 font-normal">{defaultValue === 0 ? "No" : defaultValue === 1 ? "Yes" : defaultValue}</span>
                            )}
                          </>
                        )}
                      </div>
                    </label>
                    
                    {field.isCustomInput && selectedFields.includes(field.value) && (
                      <div className="ml-[40px]">
                        <textarea
                          placeholder="Enter additional information"
                          value={AdditionalInfo}
                          onChange={(e) => setAdditionalInfo(e.target.value)}
                          className={`w-full h-40 px-3 py-2 text-text-default text-13 rounded-lg outline-none border ${
                            showErrors && selectedFields.includes("additionalInfo") && !AdditionalInfo.trim() 
                              ? "border-text-error" 
                              : "border-fieldBorder hover:border-gray-400 focus:border-text-primary-500"
                          } resize-none`}
                          maxLength={500}
                          dir="auto"
                          lang="auto"
                        />
                        <div className="flex justify-between items-center">
                          <div className="text-xs text-text-error">
                            {showErrors && selectedFields.includes("additionalInfo") && !AdditionalInfo.trim() && 
                                "This field is required when selected"
                              }
                          </div>
                          <span className={`text-xs ${
                            AdditionalInfo.length == 500
                              ? "text-text-error" 
                                : "text-text-light"
                          }`}>
                            {AdditionalInfo.length}/500
                          </span>
                        </div>

                      </div>
                    )}
                    
                    {/* {field.isDropdown && selectedFields.includes(field.value) && (
                      <div className="ml-[40px] relative" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className={`w-full px-3 py-2 text-text-default text-13 rounded-lg outline-none border flex items-center justify-between ${
                            showErrors && selectedFields.includes("docPrice") && !docPlanId
                              ? "border-text-error" 
                              : "border-fieldBorder hover:border-gray-400 focus:border-text-primary-500"
                          } ${plansLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                          disabled={plansLoading}
                        >
                          <span className={docPlanId ? "text-text-default" : "text-text-light"}>
                            {docPlanId 
                              ? planOptions.find((opt) => opt.id === docPlanId)?.label || "Select price"
                              : "Select price"}
                          </span>
                          <FiChevronDown 
                            size={16} 
                            className={`transition-transform ${isDropdownOpen ? "transform rotate-180" : ""}`}
                          />
                        </button>
                        {isDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute z-50 w-full mt-1 bg-white border border-fieldBorder rounded-lg shadow-lg max-h-60 overflow-y-auto"
                          >
                            {plansLoading ? (
                              <div className="px-3 py-4 text-center text-13 text-text-light">
                                Loading...
                              </div>
                            ) : planOptions.length > 0 ? (
                              planOptions.map((option) => (
                                <button
                                  key={option.id}
                                  type="button"
                                  onClick={() => {
                                    setDocPlanId(option.id);
                                    setIsDropdownOpen(false);
                                  }}
                                  className={`w-full px-3 py-2 text-left text-13 hover:bg-gray-50 ${
                                    docPlanId === option.id ? "bg-blue-50 text-blue-600" : "text-text-default"
                                  }`}
                                >
                                  {option.label}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-4 text-center text-13 text-text-light">
                                {plansLoading ? "Loading plans..." : plansError ? "Error loading plans" : "No plans available"}
                              </div>
                            )}
                          </motion.div>
                        )}
                        {showErrors && selectedFields.includes("docPrice") && !docPlanId && (
                          <div className="text-xs text-text-error mt-1">
                            This field is required when selected
                          </div>
                        )}
                      </div>
                    )} */}
                  </motion.div>
                );
              })}
            </div>
            <div className="flex justify-end gap-4 py-2">
              <button className="px-4 py-1.5 rounded text-13 border text-gray-700 hover:bg-gray-100" onClick={onClose}>
                Cancel
              </button>
              <Button 
                onClick={() => {
                  setShowErrors(true);
                  const hasAdditionalInfoError = selectedFields.includes("additionalInfo") && !AdditionalInfo.trim();
                  const hasDocPlanIdError = selectedFields.includes("docPrice") && !docPlanId;
                  if (selectedFields.length > 0 && !hasAdditionalInfoError && !hasDocPlanIdError) {
                    setStep("");
                  }
                }} 
                variant="primary" 
                size="small" 
                disabled={selectedFields.length === 0 } 
                isLoading={isLoading}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </motion.div>
    );
  }

  return <RenderLink onClose={onClose} empId={employeeId} fields={filteredFields.filter((f) => selectedFields.includes(f.value)).map((f) => f.id)} AdditionalInfo={AdditionalInfo} docPlanId={docPlanId || undefined} />;
};

export default memo(ShareLink);
